import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';

const DISABLE_KEY = 'ns_disable_datadog';

// Build-time kill switch (env var) — must be explicitly 'true' AND have a token
const buildTimeEnabled =
  process.env.VUE_APP_DATADOG_ENABLED === 'true'
  && !!process.env.VUE_APP_DATADOG_CLIENT_TOKEN;

// RUM additionally requires an application ID. If absent, only logs are enabled.
const rumBuildTimeEnabled =
  buildTimeEnabled && !!process.env.VUE_APP_DATADOG_RUM_APP_ID;

// Runtime kill switch (localStorage) — overrides the build flag, persists across reloads
const isDisabledAtRuntime = () => {
  try {
    return localStorage.getItem(DISABLE_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

const isLogsEnabled = () => buildTimeEnabled && !isDisabledAtRuntime();
const isRumEnabled = () => rumBuildTimeEnabled && !isDisabledAtRuntime();

const commonInitOptions = {
  clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN,
  site: process.env.VUE_APP_DATADOG_SITE || 'datadoghq.com',
  service: process.env.VUE_APP_DATADOG_SERVICE || 'ns-patient-portal',
  env: process.env.VUE_APP_DATADOG_ENV || 'development',
  version: process.env.VUE_APP_VERSION || '1.10.0',
};

if (buildTimeEnabled) {
  datadogLogs.init({
    ...commonInitOptions,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    sessionSampleRate: 100,
    beforeSend: () => {
      if (isDisabledAtRuntime()) return false;
      return true;
    },
  });
}

if (rumBuildTimeEnabled) {
  datadogRum.init({
    ...commonInitOptions,
    applicationId: process.env.VUE_APP_DATADOG_RUM_APP_ID,
    clientToken: 'pub7d75a3d2c125cc1ff7423fa6c5f33387',
    sessionSampleRate: 50,
    sessionReplaySampleRate: 10,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Manual view tracking — main.js calls trackRouteView() on each route change
    // so view names match Vue Router route names instead of raw URLs.
    trackViewsManually: true,
    beforeSend: (event) => {
      if (isDisabledAtRuntime()) return false;

      // Suppress Socket.IO long-poll transport noise — handled via socket events in main.js.
      const resourceUrl = event && event.resource && event.resource.url;
      const errorResourceUrl = event && event.error && event.error.resource && event.error.resource.url;
      const url = resourceUrl || errorResourceUrl;

      if (url && url.indexOf('/socket.io/') !== -1) {
        return false;
      }

      return true;
    },
  });
}

/**
 * Safe wrapper — never throws if Datadog is unavailable.
 * Use these instead of console.* for anything you want to see in Datadog.
 */
export const logger = {
  info: (message, context = {}) => {
    if (isLogsEnabled()) datadogLogs.logger.info(message, context);
    if (process.env.NODE_ENV !== 'production') console.info(message, context);
  },
  warn: (message, context = {}) => {
    if (isLogsEnabled()) datadogLogs.logger.warn(message, context);
    if (process.env.NODE_ENV !== 'production') console.warn(message, context);
  },
  error: (message, context = {}, error = null) => {
    if (isLogsEnabled()) datadogLogs.logger.error(message, context, error);
    if (process.env.NODE_ENV !== 'production') console.error(message, context, error);
  },
};

/**
 * Set the logged-in user context so all subsequent logs/RUM events include user info.
 * Call this after login. Call clearUserContext() on logout.
 *
 * IMPORTANT: Do NOT pass full mobile numbers, emails, or tokens — only the
 * account ID extracted from the JWT.
 */
export const setUserContext = (accountId) => {
  if (buildTimeEnabled) datadogLogs.setUser({id: String(accountId)});
  if (rumBuildTimeEnabled) datadogRum.setUser({id: String(accountId)});
};

export const clearUserContext = () => {
  if (buildTimeEnabled) datadogLogs.clearUser();
  if (rumBuildTimeEnabled) datadogRum.clearUser();
};

/**
 * Track a custom user action in RUM (analytics event).
 */
export const trackEvent = (name, context = {}) => {
  if (!isRumEnabled()) return;
  datadogRum.addAction(name, context);
};

/**
 * Track a Vue Router view change in RUM. Called from main.js router.afterEach.
 * Uses the route name when available so the view label is human-readable
 * (e.g. 'home' instead of '/portal/home').
 */
export const trackRouteView = (route) => {
  if (!isRumEnabled()) return;
  datadogRum.startView({
    name: route.name || route.path,
  });
};

/**
 * Helper to redact secrets/PII before logging.
 * Returns 'present (12 chars)' or 'NULL'/'UNDEFINED' instead of the actual value.
 */
export const redact = (value) => {
  if (value === null) return 'NULL';
  if (value === undefined) return 'UNDEFINED';
  if (value === '') return 'EMPTY_STRING';
  if (typeof value !== 'string') return `non-string (${typeof value})`;
  return `present (${value.length} chars)`;
};

/**
 * Runtime kill switch — exposed on window for easy console access.
 * Disables BOTH logs and RUM.
 *
 *   window.disableDatadog()    // off (persists across reloads)
 *   window.enableDatadog()     // on
 *   window.isDatadogEnabled()  // check current state
 *
 * Old aliases (disableDatadogLogs etc.) are kept for backwards compat.
 */
export const disableDatadog = () => {
  try {
    localStorage.setItem(DISABLE_KEY, 'true');
    console.info('[logger] Datadog DISABLED (logs + RUM, persisted)');
  } catch (e) {
    console.warn('[logger] Could not persist disable flag', e);
  }
};

export const enableDatadog = () => {
  try {
    localStorage.removeItem(DISABLE_KEY);
    console.info('[logger] Datadog ENABLED (logs + RUM)');
  } catch (e) {
    console.warn('[logger] Could not clear disable flag', e);
  }
};

export const isDatadogEnabled = () => ({
  logs: isLogsEnabled(),
  rum: isRumEnabled(),
});

// Backwards-compatible aliases
export const disableDatadogLogs = disableDatadog;
export const enableDatadogLogs = enableDatadog;
export const isDatadogLogsEnabled = () => isLogsEnabled();

if (typeof window !== 'undefined') {
  window.disableDatadog = disableDatadog;
  window.enableDatadog = enableDatadog;
  window.isDatadogEnabled = isDatadogEnabled;
  window.disableDatadogLogs = disableDatadog;
  window.enableDatadogLogs = enableDatadog;
  window.isDatadogLogsEnabled = () => isLogsEnabled();
}
